import { useState, useMemo } from 'react';
import { Search, GripVertical, Clock, CalendarPlus, ExternalLink, List, Braces, Variable } from 'lucide-react';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useRecentEvents } from '../hooks/use-recent-events';
import { useEventFieldPaths } from '../hooks/use-event-field-paths';
import type { CustomVariable } from '@/types/field-mapping-flow';
import { useAppStore } from '@/stores/app-store';
import { EVENT_APPS } from '@/config/constants';
import { getFieldLabel } from '../field-labels';

export function EventFieldsPanel() {
  const {
    recentEvents,
    selectedEventId,
    selectedEventData,
    selectEvent,
    branchEventTypeId,
  } = useRecentEvents();

  const eventTypes = useFlowBuilderStore((s) => s.eventTypes);
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);

  const fieldPaths = useEventFieldPaths(selectedEventData);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'fields' | 'response'>('fields');

  // Get custom variables for the selected branch
  const branchVariables = useMemo(() => {
    if (!selectedBranchId) return [];
    const mapping = fieldMappings[selectedBranchId];
    if (!mapping?.variables) return [];
    return Object.values(mapping.variables).sort((a, b) => (a.orderOf ?? 0) - (b.orderOf ?? 0));
  }, [selectedBranchId, fieldMappings]);

  const filteredFields = useMemo(() => {
    if (!searchQuery) return fieldPaths;
    const lower = searchQuery.toLowerCase();
    return fieldPaths.filter(
      (f) =>
        f.path.toLowerCase().includes(lower) ||
        (f.label ?? '').toLowerCase().includes(lower) ||
        String(f.sampleValue).toLowerCase().includes(lower)
    );
  }, [fieldPaths, searchQuery]);

  const formatSampleValue = (value: unknown): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') {
      return value.length > 40 ? value.substring(0, 40) + '...' : value;
    }
    return String(value);
  };

  const handleDragStart = (e: React.DragEvent, path: string) => {
    const fieldReference = `\${{{${path}}}}`;
    e.dataTransfer.setData('text/plain', fieldReference);
    e.dataTransfer.setData('application/x-field-path', path);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Build options for custom Select dropdown
  const eventOptions = useMemo(
    () =>
      recentEvents.map((event) => ({
        value: event.id,
        label: event.title,
        description: getRelativeTime(event.ct),
        icon: <Clock className="h-3 w-3 text-muted-foreground" />,
      })),
    [recentEvents]
  );

  // Find the booking URL for the current branch's event type
  const bookingUrl = useMemo(() => {
    if (!branchEventTypeId) return null;
    const eventType = eventTypes.find((et) => {
      const etId = et.id || (et.uri ? et.uri.split('/').pop() : '') || '';
      return etId === branchEventTypeId;
    });
    return eventType?.schedulingUrl || null;
  }, [branchEventTypeId, eventTypes]);

  const handleBookMeeting = () => {
    if (bookingUrl) {
      window.open(bookingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const hasEvents = recentEvents.length > 0;
  const showFields = selectedEventId && filteredFields.length > 0;

  return (
    <div className="flex h-full flex-col">
      {/* Event selector – only show if there are multiple events */}
      {hasEvents && recentEvents.length > 1 && (
        <div className="px-3 pt-2 pb-2 animate-fade-in">
          <Select
            value={selectedEventId || ''}
            onValueChange={(v) => selectEvent(v || null)}
            options={eventOptions}
            placeholder="Select recent event..."
            className="w-full"
            triggerClassName="h-auto px-2.5 py-1.5 text-xs rounded-md"
            dropdownClassName="max-w-[600px] w-max"
          />
        </div>
      )}

      {/* Single event indicator (auto-selected) */}
      {hasEvents && recentEvents.length === 1 && (
        <div className="px-3 pt-2 pb-2 animate-fade-in">
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5">
            <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate text-xs text-foreground">{recentEvents[0].title}</span>
            <span className="shrink-0 text-[10px] text-muted-foreground">{getRelativeTime(recentEvents[0].ct)}</span>
          </div>
        </div>
      )}

      {/* Search + View toggle */}
      {selectedEventId && (
        <div className="px-3 pb-2 space-y-2 animate-fade-in">
          {/* View mode toggle */}
          <div className="flex items-center rounded-md border border-border bg-muted/30 p-0.5">
            <button
              onClick={() => setViewMode('fields')}
              className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                viewMode === 'fields'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <List className="h-3 w-3" />
              Fields
            </button>
            <button
              onClick={() => setViewMode('response')}
              className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                viewMode === 'response'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Braces className="h-3 w-3" />
              Sample Response
            </button>
          </div>

          {/* Search — only in fields mode */}
          {viewMode === 'fields' && (
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search fields..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}
        </div>
      )}

      {/* Fields list / Sample response */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {/* Empty state — no events at all */}
        {!hasEvents && (
          <div className="py-8 text-center animate-fade-in" style={{ animationDelay: '100ms' }}>
            <CalendarPlus className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
            <p className="text-xs font-medium text-foreground mb-1">No recent events</p>
            <p className="text-[10px] text-muted-foreground mb-4">
              {branchEventTypeId
                ? 'No events found for this event type. Book a meeting to generate sample data.'
                : 'Trigger an event or book a meeting to see available fields.'}
            </p>
            {bookingUrl && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleBookMeeting}
              >
                <ExternalLink className="h-3 w-3" />
                Book a Meeting
              </Button>
            )}
          </div>
        )}

        {/* ── Sample Response (raw JSON view) ── */}
        {selectedEventId && viewMode === 'response' && selectedEventData && (
          <pre className="whitespace-pre-wrap break-all rounded-lg border border-border bg-muted/30 p-3 text-[10px] font-mono text-foreground leading-relaxed animate-fade-in">
            {JSON.stringify(selectedEventData, null, 2)}
          </pre>
        )}

        {selectedEventId && viewMode === 'response' && !selectedEventData && (
          <p className="py-4 text-center text-xs text-muted-foreground animate-fade-in">
            No response data available for this event
          </p>
        )}

        {/* ── Fields view ── */}
        {viewMode === 'fields' && (
          <>
            {/* Has events but selected event has no matching fields */}
            {selectedEventId && filteredFields.length === 0 && (
              <p className="py-4 text-center text-xs text-muted-foreground animate-fade-in">
                {searchQuery ? 'No fields match your search' : 'No fields found in this event'}
              </p>
            )}

            {/* Field items — staggered fade-in-up */}
            {showFields && (
              <div className="space-y-1" key={selectedEventId}>
                {filteredFields.map((field, index) => {
                  return (
                    <div
                      key={field.path}
                      draggable
                      onDragStart={(e) => handleDragStart(e, field.path)}
                      className="animate-fade-in-up group flex cursor-grab items-center gap-2 rounded-lg border border-border/60 bg-background px-2.5 py-1.5 transition-all hover:bg-accent/40 hover:border-primary/30 hover:shadow-sm active:cursor-grabbing active:scale-[0.98]"
                      style={{ animationDelay: `${Math.min(index * 20, 300)}ms` }}
                    >
                      <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />

                      <div className="min-w-0 flex-1">
                        {/* Title */}
                        <p className="truncate text-[11px] font-medium leading-tight text-foreground">
                          {field.label ?? getFieldLabel(field.path, isEventApp)}
                        </p>
                        {/* Full path */}
                        <p className="truncate text-[10px] font-mono leading-tight mt-0.5" style={{ color: 'color-mix(in oklab, #496792 60%, #989898)' }}>
                          {field.path}
                        </p>
                        {/* Sample value */}
                        <p className="truncate text-[10px] text-muted-foreground/50 mt-0.5 leading-tight">
                          {formatSampleValue(field.sampleValue)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Custom Data Variables section ── */}
            {branchVariables.length > 0 && (
              <div className="mt-3 animate-fade-in">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-purple-500/80">
                  Custom Data Variables
                </p>
                <div className="space-y-1">
                  {branchVariables
                    .filter((v) =>
                      !searchQuery || v.name.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((varItem) => (
                    <div
                      key={varItem.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, `$.variables.${varItem.id}`)}
                      className="group flex cursor-grab items-center gap-2 rounded-lg border border-purple-200/60 bg-purple-50/30 px-2.5 py-1.5 transition-all hover:bg-purple-50/60 hover:border-purple-300/50 hover:shadow-sm active:cursor-grabbing active:scale-[0.98] dark:border-purple-800/30 dark:bg-purple-950/20 dark:hover:bg-purple-950/40"
                    >
                      <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />
                      <Variable className="h-3.5 w-3.5 shrink-0 text-purple-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium leading-tight text-foreground">
                          {varItem.name}
                        </p>
                        <p className="truncate text-[10px] font-mono leading-tight mt-0.5 text-purple-400/70">
                          $.variables.{varItem.id}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Info footer */}
      <div className="border-t border-border px-3 py-2">
        <p className="text-[10px] text-muted-foreground">
          {hasEvents ? (
            <>Drag fields into mapping inputs or type <kbd className="rounded border border-border bg-muted px-1 font-mono">#</kbd> in any field input</>
          ) : bookingUrl ? (
            <>Book a meeting to generate sample event data for field mapping</>
          ) : (
            <>Trigger an incoming event to load available fields</>
          )}
        </p>
      </div>
    </div>
  );
}

// ── Helpers ──

function getRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}
